import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  signUp: (email: string, password: string, metadata?: { first_name?: string; last_name?: string }) => Promise<{ error: Error | null; data?: { user: User | null; session: Session | null } }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null; data?: { user: User | null; session: Session | null } }>;
  signOut: () => Promise<void>;
  mockSignIn?: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // 1. Check if mock authentication was active
    if (localStorage.getItem("peak_pro_mock_auth") === "true") {
      const mockUser = {
        id: "demo-owner-uuid-1111-2222",
        email: "demo@kimfloraldesigns.com",
        user_metadata: { first_name: "Demo", last_name: "Admin" },
      } as any;
      setUser(mockUser);
      setSession({ user: mockUser } as any);
      setIsLoading(false);
      return;
    }

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (localStorage.getItem("peak_pro_mock_auth") === "true") return;
        setSession(session);
        setUser(session?.user ?? null);
        setIsLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (localStorage.getItem("peak_pro_mock_auth") === "true") return;
      setSession(session);
      setUser(session?.user ?? null);
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (
    email: string, 
    password: string, 
    metadata?: { first_name?: string; last_name?: string }
  ) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error, data } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: metadata,
      },
    });
    
    return { error: error as Error | null, data };
  };

  const signIn = async (email: string, password: string) => {
    const { error, data } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    return { error: error as Error | null, data };
  };

  const mockSignIn = () => {
    const mockUser = {
      id: "demo-owner-uuid-1111-2222",
      email: "demo@kimfloraldesigns.com",
      user_metadata: { first_name: "Demo", last_name: "Admin" },
    } as any;
    localStorage.setItem("peak_pro_mock_auth", "true");
    setUser(mockUser);
    setSession({ user: mockUser } as any);
    setIsLoading(false);
  };

  const signOut = async () => {
    localStorage.removeItem("peak_pro_mock_auth");
    setUser(null);
    setSession(null);
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, isLoading, signUp, signIn, signOut, mockSignIn }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
